import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import {
  Modal,
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  Autocomplete,
  CircularProgress,
  Snackbar,
  Alert,
  Divider,
} from "@mui/material";
import PhoneInput, { isPossiblePhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { countryList, getRegionCode } from "../../utilities/countries";
import { addOrganization } from "../../store/actions";
import { validateAddressWithAPI } from '../../helpers/addressValidation';

const placeholderSx = {
  color: "#9ca3af",
  opacity: 1,
  fontSize: 14,
  fontWeight: 400,
  fontFamily: "inherit",
  fontStyle: "normal",
  letterSpacing: "normal",
  WebkitTextFillColor: "#9ca3af",
};

const filledInputSx = {
  "& .MuiFilledInput-root": {
    backgroundColor: "#f4f4f5",
    borderRadius: "10px",
    "&:before, &:after": { display: "none" },
    "&:hover": { backgroundColor: "#ececee" },
    "&.Mui-focused": {
      backgroundColor: "#f4f4f5",
      boxShadow: "0 0 0 2px rgba(17, 24, 39, 0.16)",
    },
    "&.Mui-error": { backgroundColor: "#fef2f2" },
    "&.Mui-disabled": { backgroundColor: "#f4f4f5" },
  },
  "& .MuiFilledInput-input": {
    py: 1.35,
    px: 1.5,
    fontSize: 14,
    fontWeight: 400,
    fontFamily: "inherit",
    "&::placeholder": placeholderSx,
    "&::-webkit-input-placeholder": placeholderSx,
  },
  "& .Mui-disabled .MuiFilledInput-input::placeholder, & .Mui-disabled .MuiFilledInput-input::-webkit-input-placeholder":
    placeholderSx,
  "& input[type=number]": { MozAppearance: "textfield" },
  "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button": {
    WebkitAppearance: "none",
    margin: 0,
  },
};

const phoneFieldSx = {
  display: "flex",
  alignItems: "center",
  backgroundColor: "#f4f4f5",
  borderRadius: "10px",
  px: 1.5,
  minHeight: 46,
  "&:focus-within": {
    boxShadow: "0 0 0 2px rgba(17, 24, 39, 0.16)",
  },
  "& .PhoneInput": {
    width: "100%",
    alignItems: "center",
    gap: 0,
    "--PhoneInputCountrySelect-marginRight": "4px",
    "--PhoneInputCountrySelectArrow-marginLeft": "4px",
  },
  "& .PhoneInputCountry": {
    marginRight: "4px",
    alignSelf: "center",
    flexShrink: 0,
    position: "relative",
  },
  "& .PhoneInputCountrySelect": {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    minWidth: 0,
    margin: 0,
    padding: 0,
    opacity: 0,
    border: "none",
  },
  "& .PhoneInputInput": {
    border: "none",
    outline: "none",
    background: "transparent",
    fontSize: 14,
    fontWeight: 400,
    fontFamily: "inherit",
    width: "100%",
    minWidth: 0,
    flex: 1,
    margin: 0,
    padding: 0,
    color: "inherit",
    boxShadow: "none",
    "&::placeholder": placeholderSx,
    "&::-webkit-input-placeholder": placeholderSx,
  },
};

const FieldLabel = ({ children, htmlFor, required }) => (
  <Typography
    component="label"
    htmlFor={htmlFor}
    sx={{
      display: "block",
      fontSize: 13,
      fontWeight: 600,
      color: "#111827",
      mb: 0.75,
      lineHeight: 1.3,
    }}
  >
    {children}
    {required ? (
      <Box component="span" sx={{ color: "#9ca3af", ml: 0.35 }}>
        *
      </Box>
    ) : null}
  </Typography>
);

const FormTextField = ({ id, required, InputProps, ...props }) => (
  <TextField
    id={id}
    fullWidth
    hiddenLabel
    variant="filled"
    required={required}
    InputProps={{ disableUnderline: true, ...InputProps }}
    sx={filledInputSx}
    {...props}
  />
);

const toSnackbarText = (value, fallback) => {
  if (typeof value === "string" && value) return value;
  if (value && typeof value === "object") {
    if (typeof value.message === "string") return value.message;
    if (value.message && typeof value.message === "object") {
      return toSnackbarText(value.message, fallback);
    }
  }
  return fallback;
};

const AddOrganizationModal = ({ open, onClose, onSuccess, mspOrgCode }) => {
  const dispatch = useDispatch();
  const { isAddingOrganization, addOrganizationMessage, addOrganizationError } =
    useSelector((state) => state.orgAccount);
  console.log(
    "🚀 ~ AddOrganizationModal ~ isAddingOrganization:",
    isAddingOrganization
  );

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("info");

  // Form State
  const [userFirstName, setUserFirstName] = useState("");
  const [userLastName, setUserLastName] = useState("");
  const [userPhone, setUserPhone] = useState("");
  const [phonenumberError, setPhonenumberError] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [userAddress, setUserAddress] = useState("");
  const [userCity, setUserCity] = useState("");
  const [userProvince, setUserProvince] = useState("");
  const [userZipcode, setUserZipcode] = useState("");
  const [userCountry, setUserCountry] = useState("");
  const [userComments, setUserComments] = useState("");
  const [noOfAccounts, setNoOfAccounts] = useState("");
  const [noOfUsers, setNoOfUsers] = useState("");
  const [addressValidationStatus, setAddressValidationStatus] = useState(null);
  const [addressValidationMessage, setAddressValidationMessage] = useState('');
  // const [registrationType, setRegistrationType] = useState("");

  // Clear form state
  const clearFormState = () => {
    setUserFirstName("");
    setUserLastName("");
    setUserPhone("");
    setPhonenumberError("");
    setUserEmail("");
    setCompanyName("");
    setUserAddress("");
    setUserCity("");
    setUserProvince("");
    setUserZipcode("");
    setUserCountry("");
    setUserComments("");
    setNoOfAccounts("");
    setNoOfUsers("");
    // setRegistrationType("");
  };

  // Validation function
  const isFormInvalid = () => {
    const emailRegex =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return (
      !companyName ||
      !userFirstName ||
      !userLastName ||
      !userPhone ||
      !isPossiblePhoneNumber(userPhone) ||
      !userEmail ||
      !emailRegex.test(userEmail) ||
      !userAddress ||
      !userCountry ||
      addressValidationStatus === 'invalid' ||
      !noOfAccounts ||
      !noOfUsers
      // !registrationType
    );
  };

  // Handle form submission
  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (!isFormInvalid()) {
      const newOrganization = {
        firstName: userFirstName,
        lastName: userLastName,
        phoneNumber: userPhone,
        email: userEmail,
        company: companyName,
        workAddress: userAddress,
        city: userCity,
        province: userProvince,
        zipcode: userZipcode,
        country: userCountry,
        comments: userComments,
        maxusers: noOfUsers,
        maxacounts: noOfAccounts,
        registrationtype: "SUBORG",
        mspOrgCode: mspOrgCode,
      };
      console.log("🚀 ~ handleFormSubmit ~ newOrganization:", newOrganization);
      dispatch(addOrganization(newOrganization));
    }
  };

  // Monitor success and error from Redux state
  useEffect(() => {
    if (addOrganizationMessage) {
      setSnackbarMessage(
        toSnackbarText(addOrganizationMessage, "Organization added successfully.")
      );
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      setTimeout(() => {
        onClose();
        clearFormState();
        onSuccess();
      }, 5000); // Notify parent and close modal after 2 seconds
    }

    if (addOrganizationError) {
      setSnackbarMessage(
        toSnackbarText(addOrganizationError, "Failed to add organization.")
      );
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  }, [addOrganizationMessage, addOrganizationError, onClose, onSuccess]);

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // Handle phone number validation
  const handlePhoneBlur = () => {
    if (!userPhone || (userPhone && !isPossiblePhoneNumber(userPhone))) {
      setPhonenumberError("Please enter a valid phone number.");
    } else {
      setPhonenumberError("");
    }
  };

  const validateAddress = async () => {
    if (!userAddress.trim() || !userCountry) return;

    setAddressValidationStatus(null);
    setAddressValidationMessage('Validating address...');

    const regionCode = getRegionCode(userCountry);
    if (!regionCode) {
      setAddressValidationStatus('invalid');
      setAddressValidationMessage('Please select a valid country first');
      return;
    }

    try {
      const result = await validateAddressWithAPI(
        userAddress, 
        regionCode, 
        process.env.REACT_APP_GOOGLE_MAPS_API_KEY
      );

      setAddressValidationStatus(result.isValid ? 'valid' : 'invalid');
      setAddressValidationMessage(result.message);
      
      // Update the address fields with extracted data
      setUserAddress(result.formattedAddress || userAddress);
      setUserCity(result.city);
      setUserProvince(result.state);
      setUserZipcode(result.zip);
    } catch (error) {
      setAddressValidationStatus('invalid');
      setAddressValidationMessage('Failed to validate address. Please try again.');
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="add-organization-modal"
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "calc(100% - 24px)", sm: 640 },
          maxWidth: 640,
          bgcolor: "background.paper",
          boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
          borderRadius: 3,
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            px: { xs: 2.5, sm: 3.5 },
            py: 2,
            minHeight: 72,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1.5,
            flexShrink: 0,
            boxSizing: "border-box",
            backgroundColor: "#F98125",
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              id="add-organization-modal"
              sx={{ fontSize: 16, fontWeight: 700, color: "#FFFFFF", lineHeight: 1.3 }}
            >
              Onboard Your Organization
            </Typography>
            <Typography sx={{ fontSize: 13, color: "rgba(255,255,255,0.85)", lineHeight: 1.3, mt: 0.25 }}>
              Review and confirm the company details
            </Typography>
          </Box>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            style={{
              padding: 0,
              margin: 0,
              border: "none",
              background: "transparent",
              color: "#FFFFFF",
              fontSize: 28,
              fontWeight: 700,
              lineHeight: 1,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              opacity: 1,
              boxShadow: "none",
              flexShrink: 0,
            }}
          >
            <span aria-hidden="true">×</span>
          </button>
        </Box>

        <Box
          component="form"
          onSubmit={handleFormSubmit}
          sx={{ display: "flex", flexDirection: "column", minHeight: 0, flex: 1 }}
        >
          <Box
            sx={{
              px: { xs: 2.5, sm: 3.5 },
              pt: 2.5,
              pb: 2,
              overflowY: "auto",
              flex: 1,
            }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FieldLabel htmlFor="companyName" required>
                  Company Name
                </FieldLabel>
                <FormTextField
                  id="companyName"
                  name="companyName"
                  placeholder="Enter your Company Name"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  required
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FieldLabel htmlFor="userFirstName" required>
                  First Name
                </FieldLabel>
                <FormTextField
                  id="userFirstName"
                  name="userFirstName"
                  placeholder="Enter your First Name"
                  value={userFirstName}
                  onChange={(e) => setUserFirstName(e.target.value)}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FieldLabel htmlFor="userLastName" required>
                  Last Name
                </FieldLabel>
                <FormTextField
                  id="userLastName"
                  name="userLastName"
                  placeholder="Enter your Last Name"
                  value={userLastName}
                  onChange={(e) => setUserLastName(e.target.value)}
                  required
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FieldLabel required>Phone Number</FieldLabel>
                <Box sx={phoneFieldSx}>
                  <PhoneInput
                    international
                    defaultCountry="US"
                    value={userPhone}
                    onChange={setUserPhone}
                    onBlur={handlePhoneBlur}
                    placeholder="Enter your Phone Number"
                    required
                  />
                </Box>
                {phonenumberError && (
                  <Typography color="error" variant="caption" sx={{ mt: 0.5, display: "block" }}>
                    {phonenumberError}
                  </Typography>
                )}
              </Grid>
              <Grid item xs={12} sm={6}>
                <FieldLabel htmlFor="userEmail" required>
                  Work Email Address
                </FieldLabel>
                <FormTextField
                  id="userEmail"
                  name="userEmail"
                  placeholder="Enter your Work Email Address"
                  value={userEmail}
                  onChange={(e) => setUserEmail(e.target.value)}
                  required
                />
              </Grid>

              <Grid item xs={12}>
                <FieldLabel required>Country</FieldLabel>
                <Autocomplete
                  fullWidth
                  options={countryList}
                  getOptionLabel={(option) => option?.name || ""}
                  value={countryList.find((country) => country.name === userCountry) || null}
                  onChange={(event, newValue) => {
                    setUserCountry(newValue?.name || "");
                    setUserAddress("");
                    setAddressValidationStatus(null);
                    setAddressValidationMessage("");
                  }}
                  isOptionEqualToValue={(option, value) => option.name === value?.name}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select Country"
                      required
                      hiddenLabel
                      variant="filled"
                      sx={filledInputSx}
                      InputProps={{
                        ...params.InputProps,
                        disableUnderline: true,
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props} key={option.code}>
                      {option.name}
                    </li>
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <FieldLabel htmlFor="userAddress" required>
                  Address
                </FieldLabel>
                <Box sx={{ position: "relative" }}>
                  <FormTextField
                    id="userAddress"
                    value={userAddress}
                    onChange={(e) => {
                      setUserAddress(e.target.value);
                      setAddressValidationStatus(null);
                      setAddressValidationMessage("");
                    }}
                    onBlur={validateAddress}
                    required
                    disabled={!userCountry}
                    placeholder={!userCountry ? "Please select a Country first" : "Enter your Address"}
                    error={addressValidationStatus === "invalid"}
                    InputProps={{
                      endAdornment: addressValidationStatus === "valid" && (
                        <i
                          className="mdi mdi-check-circle"
                          style={{
                            position: "absolute",
                            right: "10px",
                            color: "green",
                          }}
                        />
                      ),
                    }}
                  />
                  {addressValidationMessage && (
                    <Typography
                      variant="caption"
                      color={addressValidationStatus === "valid" ? "success" : "error"}
                      sx={{ mt: 0.5, display: "block" }}
                    >
                      {addressValidationMessage}
                    </Typography>
                  )}
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FieldLabel htmlFor="noOfAccounts" required>
                  Number of Cloud Accounts
                </FieldLabel>
                <FormTextField
                  id="noOfAccounts"
                  name="noOfAccounts"
                  type="number"
                  placeholder="e.g. 5"
                  value={noOfAccounts}
                  onChange={(e) => setNoOfAccounts(e.target.value)}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FieldLabel htmlFor="noOfUsers" required>
                  Number of Users
                </FieldLabel>
                <FormTextField
                  id="noOfUsers"
                  name="noOfUsers"
                  type="number"
                  placeholder="e.g. 50"
                  value={noOfUsers}
                  onChange={(e) => setNoOfUsers(e.target.value)}
                  required
                />
              </Grid>

              <Grid item xs={12}>
                <FieldLabel htmlFor="userComments">Comments</FieldLabel>
                <FormTextField
                  id="userComments"
                  name="userComments"
                  multiline
                  rows={3}
                  placeholder="Any additional context or notes..."
                  value={userComments}
                  onChange={(e) => setUserComments(e.target.value)}
                />
              </Grid>
            </Grid>
          </Box>

          <Divider />
          <Box
            sx={{
              px: { xs: 2.5, sm: 3.5 },
              py: 2,
              minHeight: 72,
              display: "flex",
              alignItems: "center",
              flexShrink: 0,
              boxSizing: "border-box",
            }}
          >
            <Button
              variant="contained"
              type="submit"
              fullWidth
              disabled={isAddingOrganization || isFormInvalid()}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: 14,
                px: 3,
                borderRadius: 2,
                boxShadow: "none",
                backgroundColor: "#F98125",
                "&:hover": { backgroundColor: "#E07015", boxShadow: "none" },
                "&.Mui-disabled": { backgroundColor: "#ccc", color: "#fff" },
              }}
            >
              {isAddingOrganization ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "Invite"}
            </Button>
          </Box>
        </Box>

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbarSeverity}
            sx={{ width: "100%" }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Box>
    </Modal>
  );
};

AddOrganizationModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

export default AddOrganizationModal;
